import NoAccessState from '@parkdna/ui/NoAccessState';
import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { isForbiddenError } from '@/lib/api/errors';

type ForbiddenBoundaryClassProps = {
  locationKey: string;
  variant?: 'page' | 'section';
  children: ReactNode;
};

type ForbiddenBoundaryState = {
  error: unknown;
};

class ForbiddenBoundaryClass extends Component<
  ForbiddenBoundaryClassProps,
  ForbiddenBoundaryState
> {
  state: ForbiddenBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ForbiddenBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ForbiddenBoundaryClassProps) {
    if (
      prevProps.locationKey !== this.props.locationKey &&
      this.state.error !== null
    ) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error !== null) {
      if (!isForbiddenError(this.state.error)) {
        // Not ours: let the app-level error boundary handle it.
        throw this.state.error;
      }
      if (this.props.variant === 'section') {
        return (
          <NoAccessState
            variant="section"
            description="You don't have permission to view this section. Other tabs may still be available. Contact your administrator if you need access."
          />
        );
      }
      return (
        <NoAccessState
          variant="page"
          description="You don't have permission to view this page. Contact your administrator if you need access."
        />
      );
    }
    return this.props.children;
  }
}

export default function ForbiddenBoundary({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  // location.key changes on every navigation, including search-only ones
  // (nuqs tabs, filters, dialogs), so a caught 403 never outlives the view
  // that produced it.
  return (
    <ForbiddenBoundaryClass locationKey={location.key}>
      {children}
    </ForbiddenBoundaryClass>
  );
}

/**
 * Contains a 403 to one part of a page (e.g. a tab) so the rest of the page
 * stays usable. A new `resetKey` clears a caught 403.
 */
export function SectionForbiddenBoundary({
  children,
  resetKey,
}: {
  children: ReactNode;
  resetKey: string;
}) {
  return (
    <ForbiddenBoundaryClass locationKey={resetKey} variant="section">
      {children}
    </ForbiddenBoundaryClass>
  );
}
